// src/pages/AboutPage.js
import React, { useState, useEffect } from 'react';
import './AboutPage.css';
import { Link } from 'react-router-dom';
import AboutHeroDiagram from '../components/AboutHeroDiagram';
import { fetchAuthors } from '../api/strapi';
import IEDPNexed from '../assets/IEDPNexed.png';


// Components
import FAQSection from '../components/FAQSection';
import ContactForm from '../components/ContactForm';
import Footer from '../components/Footer';

// API_URL is handled within strapi.js now, no need here unless used elsewhere
const API_URL = process.env.REACT_APP_STRAPI_URL?.replace(/\/+$/, '') || 'http://localhost:1337'; // Fallback to common default for safety during debug

export default function AboutPage() {
  // Removed unused posts state
  const [authors, setAuthors] = useState([]);
  const [error, setError] = useState(null);
    const [faqs, setFaqs] = useState([]);
  const [loadingFaqs, setLoadingFaqs] = useState(true);
  const [faqError, setFaqError] = useState(null);

  // Fetch team members (authors)
  useEffect(() => {
    fetchAuthors()
      // 1. CORRECTED: fetchAuthors returns the processed array directly
      .then(processedAuthors => {
        setAuthors(processedAuthors);
      })
      .catch(err => {
        console.error("❌ failed loading authors", err);
        setError(err.message);
      });
  }, []);

  // --- New useEffect for Fetching FAQs ---
  useEffect(() => {
    const fetchFaqs = async () => {
      setLoadingFaqs(true);
      setFaqError(null);

      const fullFetchURL = `${API_URL}/api/faqs?sort=displayOrder:asc`;
      // console.log("Fetching FAQs from URL (AboutPage):", fullFetchURL); // Keep for debugging if needed

      try {
        const response = await fetch(fullFetchURL);

        // console.log('Response Status (AboutPage):', response.status); // Keep for debugging if needed
        // console.log('Response Content-Type (AboutPage):', response.headers.get('Content-Type')); // Keep for debugging if needed

        if (!response.ok) {
          const errorText = await response.text();
          // console.error("API Error Response Text (AboutPage):", errorText.substring(0, 500));
          throw new Error(`HTTP error! Status: ${response.status}. Response: ${errorText.substring(0, 100)}...`);
        }

        const contentType = response.headers.get('Content-Type');
        if (contentType && contentType.includes('application/json')) {
          const result = await response.json();
          // console.log("Strapi API JSON Response (raw result for AboutPage):", JSON.stringify(result, null, 2));

          if (result.data && Array.isArray(result.data)) {
            const formattedFaqs = result.data
              // Ensure question and answer are strings. Answer will be Markdown.
              .filter(item => item && typeof item.question === 'string' && typeof item.answer === 'string')
              .map(item => {
                // 'item.answer' is now expected to be a Markdown string directly from Strapi.
                // NO JSON.parse() is needed here.
                // console.log(`FAQ ID ${item.id} - Answer is Markdown string:`, item.answer); // Optional debug
                return {
                  id: item.id,
                  question: item.question,
                  answer: item.answer, // Use the Markdown string answer directly
                };
              });
            setFaqs(formattedFaqs);

            // Check if any items were filtered out due to missing fields
            if (formattedFaqs.length < result.data.filter(i => i && i.question && i.answer).length) {
               console.warn("AboutPage: Some FAQ items might have been filtered out due to missing question or if the answer was not a string. Check Strapi data.");
            }

          } else {
            console.warn("AboutPage: API JSON response did not contain 'result.data' as an array or 'result.data' is missing.", result);
            setFaqs([]);
          }
        } else {
          await response.text();
          // console.error("AboutPage: Received non-JSON response:", textResponse.substring(0, 500));
          throw new Error("AboutPage: Received non-JSON response from API. Check endpoint and Strapi logs.");
        }
      } catch (error) {
        console.error("Failed to fetch or process FAQs for AboutPage:", error);
        setFaqError(error.message || "Sorry, we couldn't load the FAQs at this time.");
      } finally {
        setLoadingFaqs(false);
      }
    };

    fetchFaqs();
  }, []); // Empty dependency array: runs once on mount
  // --- End New useEffect for Fetching FAQs ---


   return (
    <main className="aboutPage">
      {error && <p className="error">Error loading posts: {error}</p>}

 {/* NEW HERO SECTION (Text-based) */}
      <section className="hero-text-wrapper">
        <div className="hero-text-overlay">
          <h1>Nexed Insight is a pioneering research and intelligence firm for institutions and organizations dedicated to increasing the impact of business education.</h1>
        </div>
      </section>


      {/* HERO */}
      <section className="aboutHeroImageWrapper">
        <div className="aboutHeroDiagram">
          <AboutHeroDiagram />
        </div>
      </section>



      {/* Mission / Intro */}
<section className="aboutMission">
  <div className="aboutTextContainer">
    <h2 className="aboutMissionStatement">
      Our mission is to advance the future of business education, maximizing
      the positive impact it has on individuals, organizations, and society.
      We are committed to developing data-driven market insights to support
      visionary leaders on all sides of business education achieve growth,
      excellence, and impact.
    </h2>
  </div>
  <div className="aboutBulletsGrid">
    <div className="aboutFeatureItem">
      <i className="fa-regular fa-graduation-cap" aria-hidden="true" />
      <h2>Empowers learning providers</h2>
      <p>
        Listen to the market in real-time—shaping client-centric offerings, and
        continuously update curricula and engagement strategies.
      </p>
    </div>
    <div className="aboutFeatureItem">
      <i className="fa-regular fa-brain-circuit" aria-hidden="true" />
      <h2>Smarter learning strategies</h2>
      <p>
        Provides organizations with insight and best practice to optimize their
        learning and development strategies—making them more measurable,
        sustainable, and impactful.
      </p>
    </div>
    <div className="aboutFeatureItem">
      <i className="fa-regular fa-bullseye-arrow" aria-hidden="true" />
      <h2>Identifies gaps</h2>
      <p>
        Identifies gaps between the needs of organizations and the current
        state of business education, developing practical recommendations to
        address them.
      </p>
    </div>
    <div className="aboutFeatureItem">
      <i className="fa-regular fa-lightbulb-on" aria-hidden="true" />
      <h2>Inspires leaders</h2>
      <p>
        Inspires leaders across the sector by documenting outstanding business
        education with a focus on innovation and impact.
      </p>
    </div>
    <div className="aboutFeatureItem aboutFeatureItem--wide">
      <i className="fa-regular fa-diagram-venn" aria-hidden="true" />
      <h2>Cross-sector collaboration</h2>
      <p>
        Strengthens ties and knowledge-sharing between talent and learning
        leaders and business education leaders — From CLOs, Heads of Learning,
        and HR leaders to deans, faculty, EdTech innovators, learning
        designers, policymakers, and industry experts.
      </p>
    </div>
  </div>
</section>

{/* Partners */}
<section className="aboutPartners">
  <div className="partnersIntro">
    <h2>Our Partners: Driving the future of business education</h2>
    <p>
      Our partners play a vital role in strengthening business education and
      driving innovation across the sector.
    </p>
  </div>

  <div className="foundingPartnerBox">
    <h4>Our Founding Partner</h4>
    <p>
      We are proud to have UNICON as a founding partner of Nexed Insight.
      Their global network and expertise help us shape world-leading research
      and strategic foresight for business education.
    </p>
    <Link to="/unicon" className="foundingPartnerLink">
      Find out more about UNICON
    </Link>
  </div>

  <div className="associatePartnersBox">
    <h4>Associate Partners</h4>
    <p>
      Nexed Insight is building a network of forward-thinking associate
      partners. Join us to align your brand with key industry themes and
      thought leadership.
    </p>
    <a href="/contact" className="associatePartnerLink">
      Contact us to explore partnerships
    </a>
  </div>
</section>

{/* Team */}
<div className="teamContainer">
  {/* Row 1: intro + image */}
  <div className="teamIntro">
    <div className="introStory">
      <h2>Our Story</h2>
      <h3>30 Years of Insight, A New Era of Shared Intelligence</h3>
      <p>
        For over 30 years IEDP has been a trusted partner and industry voice
        furthering the impact of business education worldwide.
      </p>
      <p>
        Building on that legacy, Nexed Insight delivers real-time research
        insights and strategic foresight for tomorrow’s learning leaders.
      </p>
    </div>
    <div className="introImage">
      <img src={IEDPNexed} alt="IEDP & Nexed" />
    </div>
  </div>

  {/* Row 2: Team details */}
  {authors.map(author => {
    return (
       // Use author.id for the key
       <section key={author.id} className="teamDetail">
       <div className="teamDetailLeft">
         {/* 5. CORRECTED: Use author.image directly and check if it exists */}
         {author.image && (
           <img
             className="profileImage"
             src={author.image} // Use the pre-constructed URL
             alt={author.name}   // Use lowercase name
           />
         )}
       </div>
       <div className="teamDetailRight">
         <div className="profileHeader">
           {/* 6. CORRECTED: Use lowercase properties */}
           <h3 className="profileName">{author.name}</h3>
           <div className="profileTitleWrap">
             <p className="profileTitle">{author.title}</p>
             {author.linkedin && (
               <a
                 href={author.linkedin}
                 className="profileLinkedin"
                 target="_blank"
                 rel="noopener noreferrer"
               >
                 <i className="fab fa-linkedin" />
               </a>
             )}
           </div>
         </div>
         <div className="profileBio">
           {/* 7. CORRECTED: Map directly over author.bio (already an array) */}
           {Array.isArray(author.bio) && author.bio.map((para, i) => (
             <p key={i}>{para}</p>
           ))}
         </div>
         {/* 8. CORRECTED: Use lowercase property */}
         {author.quote && (
           <p className="profileQuote">{author.quote}</p>
         )}
       </div>
      </section>
    );
  })}
</div>


      {/* --- Updated FAQ Section Rendering (This is already in your provided code and looks correct) --- */}
      <section className="aboutPageFaqSection">
        {loadingFaqs && (
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <p>Loading FAQs...</p>
          </div>
        )}
        {faqError && (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'red' }}>
            <p>{faqError}</p>
          </div>
        )}
        {!loadingFaqs && !faqError && faqs.length > 0 && (
          <FAQSection faqs={faqs} />
        )}
        {!loadingFaqs && !faqError && faqs.length === 0 && (
           (faqError ? null : <div style={{textAlign: 'center', padding: '3rem 1rem'}}><p>No frequently asked questions are available at the moment.</p></div>)
        )}
      </section>
      <section className="servicesContactSection">
          {/* New header above the form */}
          <h2 className="contactHeaderTitle">Want to know more?</h2>
          <p className="contactHeaderSubtitle">Reach out to us using the form below—we’d love to hear from you.</p>
          <ContactForm />
        </section>

      {/* FOOTER */}
      <div className="footerFullWidth">
        <Footer />
      </div>
    </main>
  );
}
